import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import "./Services.css";

const Services = () => {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [gridRef, gridVisible] = useScrollAnimation();
  const [expandedCards, setExpandedCards] = useState({});

  const toggleCard = (index) => {
    setExpandedCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const services = [
    {
      title: "Personal Insurance & Risk Protection",
      tagline:
        "We help individuals and families evaluate and structure insurance coverage that protects income, lifestyle, and long-term financial security.",
      image: "/services/personal-risk-protection.jpeg",
      includes: [
        "Life insurance planning (term and permanent, where appropriate)",
        "Disability insurance for income continuity",
        "Critical illness insurance for recovery flexibility",
        "Coverage reviews to ensure alignment with life stage",
      ],
    },
    {
      title: "Practice & Business Protection",
      tagline: "Professional practices face risks that personal insurance alone does not address.",
      image: "/services/business-protection.jpeg",
      includes: [
        "Professional overhead expense insurance",
        "Business continuity and key-person protection",
        "Insurance structuring for incorporated professionals",
      ],
    },
    {
      title: "Mortgage & Debt Protection Optimization",
      tagline:
        "Mortgage protection is often arranged at approval and rarely reviewed. Mortgage renewal presents a timely opportunity to reassess whether existing protection remains suitable.",
      image: "/services/mortgage-protection.jpeg",
      includes: [
        "Understand how lender-based mortgage insurance works",
        "Review coverage at renewal or refinancing",
        "Assess suitability for personally owned life insurance, including Universal Life, where appropriate",
        "Use convertible term insurance (YRT) when permanent coverage is not suitable",
        "Align protection with remaining debt, family needs, and affordability",
      ],
      note: "Recommendations depend on individual health, eligibility, and insurer underwriting requirements.",
    },
    {
      title: "Long-Term Protection Planning",
      tagline: "While we do not provide investment advice, we support clients by:",
      image: "https://images.unsplash.com/photo-1556740749-887f6717d7e4?w=600&h=400&fit=crop",
      includes: [
        "Reviewing insurance-based strategies used for long-term planning",
        "Ensuring protection aligns with retirement and estate considerations",
        "Coordinating with other professional advisors where appropriate",
      ],
    },
  ];

  return (
    <section id="financial-service" className="services">
      <div className="services-container">
        <div ref={headerRef} className={`services-header ${headerVisible ? "animate-in" : ""}`}>
          <h2 className="services-title">Services Snapshot</h2>
          <p className="services-description">
            Professionals, business owners, and homeowners who want to understand their options clearly, reduce unnecessary insurance costs, and ensure their protection strategies remain aligned as their financial lives and needs evolve.
          </p>
        </div>
        <div ref={gridRef} className={`services-grid ${gridVisible ? "animate-in" : ""}`}>
          {services.map((service, index) => (
            <div key={index} className="service-card">
              <div className="service-image-container">
                <img src={service.image} alt={service.title} className="service-image" />
              </div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-tagline">{service.tagline}</p>
              <div className="service-includes">
                <button
                  className="includes-toggle"
                  onClick={() => toggleCard(index)}
                  aria-expanded={expandedCards[index]}
                >
                  <strong className="includes-header">Includes:</strong>
                  {expandedCards[index] ? (
                    <ChevronUp size={18} className="toggle-icon" />
                  ) : (
                    <ChevronDown size={18} className="toggle-icon" />
                  )}
                </button>
                {expandedCards[index] && (
                  <div className="includes-content">
                    <ul className="includes-list">
                      {service.includes.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                    {service.note && (
                      <p className="service-note">
                        <strong>Important Note:</strong> {service.note}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
